import { useState, useEffect, useCallback, useRef } from 'react';
import { getOrganizationMembers } from '@/lib/firebase/queries/organizations';
import { removeMemberAndCleanupHabits } from '@/lib/firebase/queries/organizations/members';
import { getCustomersSectionsAndGroups, type CustomerGrouping } from '@/lib/firebase/queries/customer-groups';
import { deleteUser } from '@/lib/firebase/queries/users';
// import { removeMemberFromOrganization } from '@/lib/firebase/queries/organizations/members';
import { useAuth } from '@/contexts/AuthContext';
import { CustomerListTable } from './CustomerListTable';
// import { CustomerListHeader } from './CustomerListHeader';
import { AddCustomerDialog } from '@/components/manager/AddCustomerDialog';
import { ImportCustomersDialog } from '@/components/manager/ImportCustomersDialog';
import { PublicRegistrationUrlDialog } from '@/components/manager/PublicRegistrationUrlDialog';
import { AddToSectionDialog } from '@/components/manager/AddToSectionDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Plus, Link, Trash2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { CustomerWithExam } from './types';
import { loadNamatAssessments } from '@/lib/firebase/queries/namatAssessment';
import type { NamatAssessment } from '@/lib/assessment/namatAssessment';
import { getCustomersHabitPoints } from '@/lib/firebase/queries/organizations/statistics';
import { idsNeedingQuestionnaire, needsQuestionnaire } from '@/lib/assessment/questionnaireReminder';
import { RemindQuestionnaireButton } from '@/components/health/RemindQuestionnaireButton';
import { CopyQuestionnaireLinkButton } from '@/components/health/CopyQuestionnaireLinkButton';
// import { collection, query, where, getDocs } from 'firebase/firestore';
// import { db } from '@/lib/firebase/config';

interface CustomerListProps {
  organizationId: string;
  organizationName: string;
  searchQuery: string;
  onSearchChange?: (query: string) => void;
  onRefresh?: () => void;
}

export function CustomerList({ organizationId, organizationName, searchQuery, onSearchChange, onRefresh }: CustomerListProps) {
  const [customers, setCustomers] = useState<CustomerWithExam[]>([]);
  const [loading, setLoading] = useState(true);
  const latestRequest = useRef(0);
  const [sections, setSections] = useState<{ id: string; name: string }[]>([]);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('all');
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showImportCustomers, setShowImportCustomers] = useState(false);
  const [showRegistrationUrl, setShowRegistrationUrl] = useState(false);
  const [showAddToSection, setShowAddToSection] = useState(false);
  const [customerToAddToSection, setCustomerToAddToSection] = useState<CustomerWithExam | null>(null);
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  // #97: only the beneficiaries whose questionnaire is not valid.
  const [onlyNotFilled, setOnlyNotFilled] = useState(false);
  const { t, dir } = useLanguage();
  const { userData, hasRole } = useAuth();

  // Remove old calculateTotalScore function - now using dynamic calculation


  const handleDeleteCustomer = async (customerId: string): Promise<void> => {
    if (!hasRole('admin')) {
      throw new Error('Only admins can delete customers');
    }

    try {
      await deleteUser(customerId);
      // Remove the customer from the local state
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  };

  const handleRemoveFromOrganization = async (customerId: string): Promise<void> => {
    try {
      await removeMemberAndCleanupHabits(organizationId, customerId, 'customer');
      // Remove the customer from the local state
      setCustomers(prev => prev.filter(customer => customer.id !== customerId));
      toast.success(dir === 'rtl' ? 'تم إزالة المستفيد من المنظمة وحذف العادات الافتراضية' : 'Removed customer and deleted default habits');
    } catch (error) {
      console.error('Error removing customer from organization:', error);
      toast.error(dir === 'rtl' ? 'فشل في إزالة المستفيد من المنظمة' : 'Failed to remove customer from organization');
      throw error;
    }
  };

  const handleAddToSection = (customer: CustomerWithExam) => {
    setCustomerToAddToSection(customer);
    setShowAddToSection(true);
  };

  const handleDeleteAllCustomers = async (): Promise<void> => {
    if (!hasRole('admin')) {
      throw new Error('Only admins can delete customers');
    }

    try {
      setIsDeletingAll(true);
      
      // Get all customer IDs from the filtered customers
      const customerIds = shownCustomers.map(customer => customer.id);
      
      if (customerIds.length === 0) {
        toast.info(t('dashboard.manager.dashboard.customers.deleteAll.noCustomers'));
        return;
      }

      // Delete all customers in parallel
      const deletePromises = customerIds.map(customerId => deleteUser(customerId));
      await Promise.all(deletePromises);
      
      // Clear the customers list
      setCustomers([]);
      
      toast.success(
        `${t('dashboard.manager.dashboard.customers.deleteAll.success')} (${customerIds.length} customers and all related data)`
      );
    } catch (error) {
      console.error('Error deleting all customers:', error);
      toast.error(t('dashboard.manager.dashboard.customers.deleteAll.error'));
      throw error;
    } finally {
      setIsDeletingAll(false);
    }
  };

  const fetchCustomers = useCallback(async () => {
    if (!organizationId) return;
    // A slow load for the organization the manager just left must not
    // overwrite the one they switched to.
    const request = ++latestRequest.current;
    const isStale = () => request !== latestRequest.current;

    try {
      setLoading(true);
      const members = await getOrganizationMembers(organizationId);
      if (isStale()) return;
      
      // #73: every customer's Namat scale in batched reads (3 queries per 30),
      // and #117: their section and groups the same way.
      const customerIds = members.filter((m) => m.role === 'customer').map((m) => m.id);
      const assessmentsPromise = loadNamatAssessments(customerIds).catch((error) => {
        console.error('Error loading Namat scales:', error);
        return new Map<string, NamatAssessment>();
      });
      const groupingsPromise = getCustomersSectionsAndGroups(customerIds, organizationId).catch((error) => {
        console.error('Error loading sections and groups:', error);
        return new Map<string, CustomerGrouping>();
      });

      const membersWithScores = await Promise.all(
        members.map(async (member) => {
          try {
            if (member.role === 'customer') {
              // One section lookup serves both the name and the id.
              const [assessments, groupings] = await Promise.all([assessmentsPromise, groupingsPromise]);
              const sectionInfo = groupings.get(member.id)?.section ?? null;
              const groupNames = groupings.get(member.id)?.groupNames ?? [];
              // Missing = its chunk failed to load; shown as no scale.
              const assessment = assessments.get(member.id);
              
              return { 
                ...member, 
                // Spec #86: the Namat scale only when a valid questionnaire exists.
                namatScore: assessment?.status === 'valid' ? assessment.scale?.percent : undefined,
                assessment,
                sectionName: sectionInfo ? sectionInfo.sectionName : 'Not in a section',
                sectionId: sectionInfo?.sectionId,
                groupNames,
                lastExam: assessment?.filledAt ?? undefined,
                lastExamStatus: assessment?.examDone ? 'completed' : undefined
              } as CustomerWithExam;
            }
            // If we get here, member is not a customer
            return { ...member, lastExam: undefined, lastExamStatus: undefined, sectionName: undefined } as CustomerWithExam;
          } catch (error) {
            console.error('❌ Error loading dynamic scale for:', member.firstNameEn, error);
            return { ...member, lastExam: undefined, lastExamStatus: undefined, sectionName: undefined } as CustomerWithExam;
          }
        })
      );
      
      if (isStale()) return;
      setCustomers(membersWithScores as CustomerWithExam[]);

      // #70: habit points in batched reads, filled in after the table shows.
      getCustomersHabitPoints(customerIds)
        .then((points) => {
          if (isStale()) return;
          setCustomers((prev) => prev.map((c) => (c.role === 'customer' ? { ...c, habitPoints: points.get(c.id) ?? null } : c)));
        })
        .catch((error) => console.error('Error loading habit points:', error));

      // Build sections list from customers (unique sectionId/name pairs)
      const uniqueSections = new Map<string, string>();
      for (const c of membersWithScores as CustomerWithExam[]) {
        if (c.sectionId && c.sectionName) {
          uniqueSections.set(c.sectionId, c.sectionName);
        }
      }
      setSections(Array.from(uniqueSections.entries()).map(([id, name]) => ({ id, name })));
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to fetch customers');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleSearchChange = (query: string) => {
    console.log('🔍 Manager dashboard search query changed:', query);
    setLocalSearchQuery(query);
    onSearchChange?.(query);
  };

  const filteredCustomers = customers
    .filter(customer => customer.role === 'customer')
    .filter(customer => 
      selectedSectionId === 'all' 
        ? true 
        : selectedSectionId === 'none' 
          ? !customer.sectionId 
          : customer.sectionId === selectedSectionId
    )
    .filter(customer => {
      if (!localSearchQuery.trim()) return true;
      
      const searchTerm = localSearchQuery.toLowerCase().trim();
      
      // Handle null/undefined values safely
      const firstNameEn = customer.firstNameEn || '';
      const lastNameEn = customer.lastNameEn || '';
      const firstNameAr = customer.firstNameAr || '';
      const lastNameAr = customer.lastNameAr || '';
      const email = customer.email || '';
      const phone = customer.phone || '';
      
      const nameEn = `${firstNameEn} ${lastNameEn}`.toLowerCase().trim();
      const nameAr = `${firstNameAr} ${lastNameAr}`.toLowerCase().trim();
      const emailLower = email.toLowerCase().trim();
      const phoneLower = phone.toLowerCase().trim();
      
      const matches = nameEn.includes(searchTerm) || 
                     nameAr.includes(searchTerm) || 
                     emailLower.includes(searchTerm) || 
                     phoneLower.includes(searchTerm);
      
      if (matches) {
        console.log('✅ Manager dashboard match found:', customer.firstNameEn, customer.lastNameEn, 'for query:', searchTerm);
      } else {
        console.log('❌ Manager dashboard no match for:', customer.firstNameEn, customer.lastNameEn, 'searching for:', searchTerm);
        console.log('   nameEn:', nameEn, 'nameAr:', nameAr, 'email:', emailLower, 'phone:', phoneLower);
      }
      
      return matches;
    })
    // 🔧 FIX: Remove duplicates based on customer ID
    .filter((customer, index, array) => 
      array.findIndex(c => c.id === customer.id) === index
    );

  // Debug search results
  console.log('🔍 Manager dashboard search results:', filteredCustomers.length, 'of', customers.filter(c => c.role === 'customer').length, 'customers');
  console.log('🔍 Search query:', localSearchQuery);

  // #97: «لم يملأ الاستبيان» — not done or expired — within the current
  // section and search. The count, the filter and the reminder share it.
  const notFilledIds = idsNeedingQuestionnaire(filteredCustomers, (c) => c.id, (c) => c.assessment);
  const shownCustomers = onlyNotFilled
    ? filteredCustomers.filter((c) => needsQuestionnaire(c.assessment))
    : filteredCustomers;
  const canRemind = hasRole('manager') || hasRole('admin');

  return (
    <div className="space-y-6" dir={dir}>
      {/* Header with Search, Section Filter and Action Buttons */}
      <div className={`flex items-center justify-between gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
        {/* Search Bar */}
        <div className="relative flex-1 max-w-sm h-9">
          <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground ${dir === 'rtl' ? 'right-3' : 'left-3'}`} />
          <Input
            placeholder={t('dashboard.manager.dashboard.customers.searchPlaceholder')}
            className={`${dir === 'rtl' ? 'pr-9' : 'pl-9'} h-9`}
            value={localSearchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        
        {/* Section Filter + Action Buttons */}
        <div className={`flex items-center gap-2 ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}>
          <div className="relative h-9">
            <Select value={selectedSectionId} onValueChange={setSelectedSectionId}>
              <SelectTrigger className={`h-9 w-[200px] ${dir === 'rtl' ? 'text-right pl-8' : 'text-left pr-8'}`}>
                <SelectValue placeholder={dir === 'rtl' ? 'جميع الأقسام' : 'All Sections'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{dir === 'rtl' ? 'جميع الأقسام' : 'All Sections'}</SelectItem>
                <SelectItem value="none">{dir === 'rtl' ? 'بدون قسم' : 'No Section'}</SelectItem>
                {sections.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button 
            variant="outline"
            onClick={() => setShowRegistrationUrl(true)}
            className={`${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
          >
            <Link className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            {t('dashboard.manager.dashboard.customers.createRegistrationLink')}
          </Button>
          <CopyQuestionnaireLinkButton />
          
          {/* Delete All by Admin Button - Only for Admins */}
          <Button
            variant={onlyNotFilled ? 'default' : 'outline'}
            onClick={() => setOnlyNotFilled((v) => !v)}
            className={onlyNotFilled ? 'bg-[#004250] hover:bg-[#003240] text-white' : ''}
            aria-pressed={onlyNotFilled}
            data-testid="filter-questionnaire-not-filled"
          >
            {dir === 'rtl' ? 'لم يملأ الاستبيان' : 'Questionnaire not filled'} ({notFilledIds.length})
          </Button>
          {onlyNotFilled && canRemind && (
            <RemindQuestionnaireButton organizationId={organizationId} userIds={notFilledIds} />
          )}

          {hasRole('admin') && shownCustomers.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive"
                  disabled={isDeletingAll}
                  className={`${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
                >
                  {isDeletingAll ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Trash2 className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                  )}
                  {isDeletingAll 
                    ? t('dashboard.manager.dashboard.customers.deleteAll.deleting')
                    : t('dashboard.manager.dashboard.customers.deleteAll.button')
                  }
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-4xl">
                <AlertDialogHeader className="text-center pb-6">
                  <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="h-8 w-8 text-red-600" />
                  </div>
                  <AlertDialogTitle className="text-2xl font-bold text-gray-900">
                    {dir === 'rtl' ? 'تأكيد حذف جميع المستفيدين' : 'Confirm Delete All Customers'}
                  </AlertDialogTitle>
                  <p className="text-gray-600 mt-2">
                    {dir === 'rtl' 
                      ? 'هذا الإجراء سيحذف جميع المستفيدين نهائياً من النظام'
                      : 'This action will permanently delete all customers from the system'
                    }
                  </p>
                </AlertDialogHeader>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-6">
                  {/* Left Column - Warning and Details */}
                  <div className="space-y-6">
                    {/* Warning Section */}
                    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <span className="text-red-600 text-lg font-bold">!</span>
                          </div>
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold text-red-800 mb-2">
                            {dir === 'rtl' ? 'تحذير: إجراء لا يمكن التراجع عنه' : 'Warning: Irreversible Action'}
                          </h4>
                          <p className="text-red-700 leading-relaxed">
                            {dir === 'rtl' 
                              ? 'هذا الإجراء سيحذف جميع المستفيدين نهائياً من النظام. لا يمكن استرداد البيانات المحذوفة.'
                              : 'This action will permanently delete all customers from the system. Deleted data cannot be recovered.'
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Details Section */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                          <span className="text-gray-600 text-sm font-bold">i</span>
                        </div>
                        {dir === 'rtl' ? 'تفاصيل الحذف' : 'Deletion Details'}
                      </h4>
                      <div className="space-y-3 text-gray-700">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="font-medium">{dir === 'rtl' ? 'عدد المستفيدين:' : 'Customers:'}</span>
                          <span className="font-bold text-red-600 text-lg">{shownCustomers.length}</span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="font-medium">{dir === 'rtl' ? 'نوع الحذف:' : 'Deletion type:'}</span>
                          <span className="font-semibold text-red-600">{dir === 'rtl' ? 'نهائي' : 'Permanent'}</span>
                        </div>
                        <div className="flex justify-between items-center py-2">
                          <span className="font-medium">{dir === 'rtl' ? 'المستخدم المسؤول:' : 'Admin user:'}</span>
                          <span className="font-semibold text-gray-800">
                            {userData?.firstNameEn} {userData?.lastNameEn}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column - Data Impact and Confirmation */}
                  <div className="space-y-6">
                    {/* What will be deleted */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-yellow-800 mb-4 flex items-center gap-2">
                        <div className="w-6 h-6 bg-yellow-200 rounded-full flex items-center justify-center">
                          <span className="text-yellow-700 text-sm font-bold">⚠</span>
                        </div>
                        {dir === 'rtl' ? 'البيانات التي سيتم حذفها:' : 'Data that will be deleted:'}
                      </h4>
                      <ul className="text-yellow-700 space-y-3">
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span>{dir === 'rtl' ? 'معلومات المستفيدين الشخصية' : 'Customer personal information'}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span>{dir === 'rtl' ? 'سجل العادات والتتبع' : 'Habits and tracking records'}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span>{dir === 'rtl' ? 'نتائج الفحوصات' : 'Exam results'}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span>{dir === 'rtl' ? 'نقاط المنافسة' : 'Competition points'}</span>
                        </li>
                        <li className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                          <span>{dir === 'rtl' ? 'عضوية المجموعات' : 'Group memberships'}</span>
                        </li>
                      </ul>
                    </div>

                    {/* Final confirmation */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center">
                          <span className="text-blue-700 text-sm font-bold">✓</span>
                        </div>
                        {dir === 'rtl' ? 'التأكيد النهائي' : 'Final Confirmation'}
                      </h4>
                      <p className="text-blue-700 mb-4 leading-relaxed">
                        {dir === 'rtl' 
                          ? 'للمتابعة، يرجى كتابة "DELETE ALL" في الحقل أدناه:'
                          : 'To proceed, please type "DELETE ALL" in the field below:'
                        }
                      </p>
                      <input
                        type="text"
                        id="deleteConfirmation"
                        placeholder={dir === 'rtl' ? 'اكتب "DELETE ALL"' : 'Type "DELETE ALL"'}
                        className="w-full px-4 py-3 border border-blue-300 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                        onChange={(e) => {
                          const confirmButton = document.getElementById('confirmDeleteButton') as HTMLButtonElement;
                          if (confirmButton) {
                            confirmButton.disabled = e.target.value !== 'DELETE ALL';
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <AlertDialogFooter className="pt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <AlertDialogCancel className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors">
                      {dir === 'rtl' ? 'إلغاء' : 'Cancel'}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      id="confirmDeleteButton"
                      onClick={handleDeleteAllCustomers}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                      disabled={true}
                    >
                      <Trash2 className="h-5 w-5" />
                      {dir === 'rtl' ? 'حذف جميع المستفيدين' : 'Delete All Customers'}
                    </AlertDialogAction>
                  </div>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <Button
            variant="outline"
            onClick={() => setShowImportCustomers(true)}
            className={`${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
          >
            <Plus className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            {dir === 'rtl' ? 'استيراد من Excel' : 'Import from Excel'}
          </Button>

          <Button
            onClick={() => setShowAddCustomer(true)}
            className={`bg-[#7dda55] hover:bg-[#6dc447] text-white ${dir === 'rtl' ? 'flex-row-reverse' : ''}`}
          >
            <Plus className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
            {t('dashboard.manager.dashboard.customers.addCustomer')}
          </Button>
        </div>
      </div>

      {/* Customer Table */}
      <CustomerListTable 
        customers={shownCustomers}
        isLoading={loading}
        onDeleteCustomer={handleDeleteCustomer}
        onRemoveFromOrganization={handleRemoveFromOrganization}
        onAddToSection={handleAddToSection}
      />

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={showAddCustomer}
        onOpenChange={setShowAddCustomer}
        organizationId={organizationId}
        onSuccess={() => {
          fetchCustomers();
          onRefresh?.();
        }}
      />

      {/* Import Customers Dialog */}
      <ImportCustomersDialog
        open={showImportCustomers}
        onOpenChange={setShowImportCustomers}
        organizationId={organizationId}
        onSuccess={() => {
          fetchCustomers();
          onRefresh?.();
        }}
      />

      {/* Public Registration URL Dialog */}
      <PublicRegistrationUrlDialog
        open={showRegistrationUrl}
        onOpenChange={setShowRegistrationUrl}
        organizationId={organizationId}
        organizationName={organizationName}
      />

      {/* Add to Section Dialog */}
      <AddToSectionDialog
        customer={customerToAddToSection}
        organizationId={organizationId}
        open={showAddToSection}
        onOpenChange={(open) => {
          setShowAddToSection(open);
          if (!open) {
            setCustomerToAddToSection(null);
          }
        }}
        onSuccess={() => {
          fetchCustomers();
          onRefresh?.();
        }}
      />
    </div>
  );
}

export * from './types';